import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  Grid3X3,
  List,
  Kanban,
  SortAsc,
  SortDesc,
  Star,
  Pin,
  Archive,
  Clock,
  Tag,
  Folder,
  MoreVertical,
  Edit,
  Copy,
  Trash2,
  Share2,
  BookOpen,
  FileText,
  Lightbulb,
  Target
} from 'lucide-react';
import { RootState } from '../store';
import {
  duplicateNote,
  selectNote,
  setSearchQuery,
  setFilterBy,
  setSortBy,
  setSortOrder,
  setViewMode,
  fetchNotes,
  fetchFolders,
  createNoteAsync,
  updateNoteAsync,
  deleteNoteAsync,
  toggleNotePinnedAsync,
  toggleNoteFavoriteAsync,
  toggleNoteArchivedAsync,
  createFolderAsync,
  startRealtimeSync,
  stopRealtimeSync,
  selectFolder
} from '../store/slices/notesSlice';
import { Note, NoteFolder } from '../types';
import ModernNoteCreator from '../components/notes/ModernNoteCreator';
import NoteDetail from '../components/notes/NoteDetail';

const Notes: React.FC = () => {
  const dispatch = useDispatch();
  const {
    notes,
    folders,
    templates,
    selectedNote,
    selectedFolder,
    searchQuery,
    filterBy,
    sortBy,
    sortOrder,
    viewMode
  } = useSelector((state: RootState) => state.notes);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModernCreator, setShowModernCreator] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [selectedNoteMenu, setSelectedNoteMenu] = useState<string | null>(null);
  const [showNoteDetail, setShowNoteDetail] = useState(false);
  const [detailNote, setDetailNote] = useState<Note | null>(null);

  // Start realtime sync on component mount and cleanup on unmount
  useEffect(() => {
    let isMounted = true;
    
    // Start realtime sync which will automatically fetch and listen to changes
    if (isMounted) {
      dispatch(startRealtimeSync() as any);
    }
    
    // Cleanup function to stop realtime sync when component unmounts
    return () => {
      isMounted = false;
      try {
        dispatch(stopRealtimeSync() as any);
      } catch (error) {
        console.warn('Error during cleanup:', error);
      }
    };
  }, [dispatch]);

  // Filter and sort notes
  const filteredAndSortedNotes = useMemo(() => {
    let filtered = notes;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(note =>
        note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply category filter
    switch (filterBy) {
      case 'pinned':
        filtered = filtered.filter(note => note.isPinned);
        break;
      case 'favorites':
        filtered = filtered.filter(note => note.isFavorite);
        break;
      case 'archived':
        filtered = filtered.filter(note => note.isArchived);
        break;
      case 'recent':
        filtered = filtered.filter(note => {
          const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
          return new Date(note.updatedAt) > dayAgo;
        });
        break;
      default:
        filtered = filtered.filter(note => !note.isArchived);
    }

    // Apply folder filter
    if (selectedFolder) {
      filtered = filtered.filter(note => selectedFolder.noteIds.includes(note.id));
    }

    // Sort notes
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'created':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'updated':
        default:
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [notes, searchQuery, filterBy, sortBy, sortOrder, selectedFolder]);

  const handleCreateNote = () => {
    const newNote = {
      title: 'Yeni Not',
      content: '',
      subjectId: undefined,
      topicId: undefined,
      tags: [],
      color: '#3B82F6',
      isPinned: false,
      isArchived: false,
      isFavorite: false,
      attachments: [],
      reminders: [],
      collaborators: []
    };
    dispatch(createNoteAsync(newNote) as any);
    setShowCreateModal(false);
  };

  const handleCreateFromTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const newNote = {
        title: template.name,
        content: template.content,
        subjectId: undefined,
        topicId: undefined,
        tags: [...template.tags],
        color: '#3B82F6',
        isPinned: false,
        isArchived: false,
        isFavorite: false,
        attachments: [],
        reminders: [],
        collaborators: []
      };
      dispatch(createNoteAsync(newNote) as any);
      setShowCreateModal(false);
    }
  };

  const handleCreateFolder = () => {
    const newFolder = {
      name: 'Yeni Klasör',
      description: '',
      color: '#6B7280',
      noteIds: [],
      isShared: false
    };
    dispatch(createFolderAsync(newFolder) as any);
    setShowFolderModal(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Az önce';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} saat önce`;
    } else if (diffInHours < 48) {
      return 'Dün';
    } else {
      return date.toLocaleDateString('tr-TR');
    }
  };

  const getSubjectColor = (subjectId?: string) => {
    const colors: { [key: string]: string } = {
      matematik: '#3B82F6',
      turkce: '#EF4444',
      fen: '#10B981',
      sosyal: '#F59E0B',
      ingilizce: '#8B5CF6'
    };
    return colors[subjectId || ''] || '#6B7280';
  };

  const NoteCard: React.FC<{ note: Note }> = ({ note }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -2 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-3 sm:p-4 cursor-pointer hover:shadow-xl hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 relative group"
      onClick={() => {
        setDetailNote(note);
        setShowNoteDetail(true);
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <div className="flex items-center gap-1.5 sm:gap-2">
          {note.isPinned && <Pin className="text-orange-500 sm:w-3.5 sm:h-3.5" />}
          {note.isFavorite && <Star className="text-yellow-500 sm:w-3.5 sm:h-3.5" />}
          <div
            className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
            style={{ backgroundColor: note.color }}
          />
        </div>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedNoteMenu(selectedNoteMenu === note.id ? null : note.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-all"
          >
            <MoreVertical size={14} className="sm:w-4 sm:h-4" />
          </button>
          
          {selectedNoteMenu === note.id && (
            <div className="absolute right-0 top-6 sm:top-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200/50 dark:border-gray-700/50 py-1 z-10 min-w-[140px] sm:min-w-[150px]">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(selectNote(note.id));
                  setSelectedNoteMenu(null);
                }}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/80 flex items-center gap-1.5 sm:gap-2 transition-colors text-sm"
              >
                <Edit size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span>Düzenle</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(duplicateNote(note.id));
                  setSelectedNoteMenu(null);
                }}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/80 flex items-center gap-1.5 sm:gap-2 transition-colors text-sm"
              >
                <Copy size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span>Kopyala</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(toggleNotePinnedAsync(note.id) as any);
                  setSelectedNoteMenu(null);
                }}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/80 flex items-center gap-1.5 sm:gap-2 transition-colors text-sm"
              >
                <Pin size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span className="truncate">{note.isPinned ? 'Sabitlemeyi Kaldır' : 'Sabitle'}</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(toggleNoteFavoriteAsync(note.id) as any);
                  setSelectedNoteMenu(null);
                }}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/80 flex items-center gap-1.5 sm:gap-2 transition-colors text-sm"
              >
                <Star size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span className="truncate">{note.isFavorite ? 'Favorilerden Çıkar' : 'Favorilere Ekle'}</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(toggleNoteArchivedAsync(note.id) as any);
                  setSelectedNoteMenu(null);
                }}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/80 flex items-center gap-1.5 sm:gap-2 transition-colors text-sm"
              >
                <Archive size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span className="truncate">{note.isArchived ? 'Arşivden Çıkar' : 'Arşivle'}</span>
              </button>
              <hr className="my-1 border-gray-200 dark:border-gray-600" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(deleteNoteAsync(note.id) as any);
                  setSelectedNoteMenu(null);
                }}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-left hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 flex items-center gap-1.5 sm:gap-2 text-sm"
              >
                <Trash2 size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span>Sil</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2 line-clamp-2 text-sm sm:text-base">
        {note.title}
      </h3>

      {/* Content Preview */}
      <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm line-clamp-3 mb-2 sm:mb-3">
        {note.content.replace(/[#*`]/g, '').substring(0, 150)}
      </p>

      {/* Tags */}
      {note.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
          {note.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full"
            >
              {tag}
            </span>
          ))}
          {note.tags.length > 3 && (
            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
              +{note.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <Clock size={10} className="sm:w-3 sm:h-3 flex-shrink-0" />
          <span className="truncate">{formatDate(note.updatedAt)}</span>
        </div>
        {note.subjectId && (
          <div
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-white text-xs flex-shrink-0 ml-2"
            style={{ backgroundColor: getSubjectColor(note.subjectId) }}
          >
            {note.subjectId}
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Notlarım
                </h1>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mt-1">
                  {filteredAndSortedNotes.length} not bulundu
                </p>
              </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setShowFolderModal(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300/50 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50/80 dark:hover:bg-gray-700/80 transition-all backdrop-blur-sm text-sm sm:text-base"
              >
                <Folder size={16} className="sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Yeni Klasör</span>
                <span className="sm:hidden">Klasör</span>
              </button>
              <button
                onClick={() => setShowModernCreator(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                <Plus size={16} className="sm:w-5 sm:h-5" />
                <span>Yeni Not</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Sidebar */}
          <div className="w-full lg:w-64 lg:flex-shrink-0">
            {/* Search */}
            <div className="relative mb-4 lg:mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Notlarda ara..."
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                className="w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-200/50 dark:border-gray-700/50 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>

            {/* Filters */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50 p-3 sm:p-4 mb-4 lg:mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm sm:text-base">Filtreler</h3>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                {[
                  { key: 'all', label: 'Tüm Notlar', icon: FileText },
                  { key: 'pinned', label: 'Sabitlenmiş', icon: Pin },
                  { key: 'favorites', label: 'Favoriler', icon: Star },
                  { key: 'recent', label: 'Son Güncellenen', icon: Clock },
                  { key: 'archived', label: 'Arşivlenmiş', icon: Archive }
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => dispatch(setFilterBy(key as any))}
                    className={`w-full flex items-center gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-left transition-colors text-sm sm:text-base ${
                      filterBy === key
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <Icon size={14} className="sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Folders */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50 p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm sm:text-base">Klasörler</h3>
              <div className="space-y-2">
                <button
                  onClick={() => dispatch(selectFolder(null))}
                  className={`w-full flex items-center gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-left transition-colors text-sm sm:text-base ${
                    !selectedFolder
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <FileText size={14} className="sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">Tüm Notlar</span>
                </button>
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => dispatch(selectFolder(folder.id))}
                    className={`w-full flex items-center justify-between px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-left transition-colors text-sm sm:text-base ${
                      selectedFolder?.id === folder.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Folder size={14} className="sm:w-4 sm:h-4 flex-shrink-0" style={{ color: folder.color }} />
                      <span className="truncate">{folder.name}</span>
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                      {folder.noteIds.length}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Sırala:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => dispatch(setSortBy(e.target.value as any))}
                    className="px-2.5 sm:px-3 py-1 sm:py-1.5 border border-gray-200/50 dark:border-gray-700/50 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-white text-xs sm:text-sm"
                  >
                    <option value="updated">Son Güncelleme</option>
                    <option value="created">Oluşturma Tarihi</option>
                    <option value="title">Başlık</option>
                  </select>
                  <button
                    onClick={() => dispatch(setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'))}
                    className="p-1 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 rounded transition-colors"
                  >
                    {sortOrder === 'asc' ? <SortAsc size={14} className="sm:w-4 sm:h-4" /> : <SortDesc size={14} className="sm:w-4 sm:h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2">
                  {[
                    { mode: 'grid', icon: Grid3X3 },
                    { mode: 'list', icon: List },
                    { mode: 'kanban', icon: Kanban }
                  ].map(({ mode, icon: Icon }) => (
                    <button
                      key={mode}
                      onClick={() => dispatch(setViewMode(mode as any))}
                      className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                        viewMode === mode
                          ? 'bg-blue-100/80 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-md'
                          : 'hover:bg-gray-100/80 dark:hover:bg-gray-700/80 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      <Icon size={14} className="sm:w-4 sm:h-4" />
                    </button>
                  ))}
                </div>
            </div>

            {/* Notes Grid */}
            <div className={`grid gap-3 sm:gap-4 ${
              viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'
            }`}>
              <AnimatePresence>
                {filteredAndSortedNotes.map((note) => (
                  <NoteCard key={note.id} note={note} />
                ))}
              </AnimatePresence>
            </div>

            {/* Empty State */}
            {filteredAndSortedNotes.length === 0 && (
              <div className="text-center py-8 sm:py-12 px-4">
                <BookOpen className="mx-auto text-gray-400 mb-3 sm:mb-4 sm:w-16 sm:h-16" size={48} />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Henüz not yok
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 max-w-md mx-auto">
                  İlk notunuzu oluşturarak başlayın
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all text-sm sm:text-base"
                >
                  İlk Notumu Oluştur
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Note Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-md md:max-w-lg shadow-2xl border border-gray-200/50 dark:border-gray-700/50 max-h-[95vh] overflow-y-auto">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Yeni Not Oluştur
            </h3>
            <div className="space-y-3 sm:space-y-4">
              <button
                onClick={handleCreateNote}
                className="w-full p-3 sm:p-4 border border-gray-200/50 dark:border-gray-700/50 rounded-lg hover:bg-gray-50/80 dark:hover:bg-gray-700/80 transition-all flex items-center gap-2 sm:gap-3 backdrop-blur-sm"
              >
                <FileText className="text-blue-500 flex-shrink-0 sm:w-5 sm:h-5" size={16} />
                <div className="text-left min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">Boş Not</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Sıfırdan başla</div>
                </div>
              </button>
              
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleCreateFromTemplate(template.id)}
                  className="w-full p-3 sm:p-4 border border-gray-200/50 dark:border-gray-700/50 rounded-lg hover:bg-gray-50/80 dark:hover:bg-gray-700/80 transition-all flex items-center gap-2 sm:gap-3 backdrop-blur-sm"
                >
                  {template.category === 'study' ? (
                    <BookOpen className="text-green-500 flex-shrink-0 sm:w-5 sm:h-5" size={16} />
                  ) : template.category === 'exam' ? (
                    <Target className="text-orange-500 flex-shrink-0 sm:w-5 sm:h-5" size={16} />
                  ) : (
                    <Lightbulb className="text-yellow-500 flex-shrink-0 sm:w-5 sm:h-5" size={16} />
                  )}
                  <div className="text-left min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white text-sm sm:text-base truncate">{template.name}</div>
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{template.description}</div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 sm:gap-3 mt-4 sm:mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm sm:text-base"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-md shadow-2xl border border-gray-200/50 dark:border-gray-700/50">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Yeni Klasör Oluştur
            </h3>
            <div className="space-y-3 sm:space-y-4">
              <button
                onClick={handleCreateFolder}
                className="w-full p-3 sm:p-4 border border-gray-200/50 dark:border-gray-700/50 rounded-lg hover:bg-gray-50/80 dark:hover:bg-gray-700/80 transition-all flex items-center gap-2 sm:gap-3 backdrop-blur-sm"
              >
                <Folder className="text-blue-500 flex-shrink-0 sm:w-5 sm:h-5" size={16} />
                <div className="text-left min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">Yeni Klasör</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Notları organize et</div>
                </div>
              </button>
            </div>
            <div className="flex justify-end gap-2 sm:gap-3 mt-4 sm:mt-6">
              <button
                onClick={() => setShowFolderModal(false)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm sm:text-base"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modern Note Creator */}
      {showModernCreator && (
        <ModernNoteCreator
          onClose={() => setShowModernCreator(false)}
          folderId={selectedFolder?.id}
        />
      )}

      {/* Note Editor */}
      {selectedNote && (
        <ModernNoteCreator
          editNote={notes.find(n => n.id === selectedNote)}
          mode="edit"
          onClose={() => dispatch(selectNote(null))}
          folderId={selectedFolder?.id}
        />
      )}

      {/* Note Detail */}
      {showNoteDetail && detailNote && (
        <NoteDetail
          note={detailNote}
          onClose={() => {
            setShowNoteDetail(false);
            setDetailNote(null);
          }}
          onEdit={() => {
            setShowNoteDetail(false);
            setDetailNote(null);
            dispatch(selectNote(detailNote.id));
          }}
        />
      )}
    </div>
  );
};

export default Notes;